<template>
  <div class="soar-container">
    <ServiceCardSkeleton v-if="isLoading" />
    <template v-else>
      <div class="w-full my-8">
        <div class="text-left w-full mx-4">
          <span class="text-xl text-primary font-semibold py-8">
            {{ serviceShortName }}
          </span>
        </div>
        <div class="border-2 border-gray-100 my-6">
          <div class="w-full flex flex-col items-center bg-gray-100">
            <div class="w-full px-8 py-6">
              <div class="flex gap-6">
                <span class="text-gray-600 font-normal">
                  {{ headerText.text?.serviceTransactionName }}
                </span>
                <div
                  class="w-1/3 flex items-center justify-center"
                  v-if="serviceCardLanguage?.modificationDate"
                >
                  <label>{{ $t('modificationDate') }}</label>
                  {{ serviceCardLanguage?.modificationDate }}
                </div>
              </div>
              <div class="w-full">
                <a
                  href="default.html#/login"
                  class="bg-primary"
                  v-if="showServiceCardVideo"
                  >{{ $t('startService') }}</a
                >
              </div>
            </div>
          </div>

          <div class="flex items-center">
            <div class="flex flex-col w-1/4">
              <label class="text-gray-600 uppercase px-4">
                {{ $t('serviceChannelsDescription') }}
              </label>
              <div class="flex items-center box-border">
                <div v-for="(service, index) in serviceChannel" :key="index">
                  <i v-if="showServiceChannels(service.id)">
                    {{ service?.text }}
                  </i>
                </div>
              </div>
            </div>

            <div class="w-full md:w-1/3 px-4 py-3 border-l border-brand-gray-300 flex gap-x-4">
              <div class="flex flex-col gap-y-2">
                <span>{{ $t('toShareService') }}</span>
                <div class="flex flex-col md:flex-row gap-3">
                  <button class="flex gap-x-2 text-primary text-start text-xs cursor-pointer">
                    <faPaperPlane class="w-3 fill-primary"></faPaperPlane>
                    {{ $t('shareService') }}
                  </button>
                  <a
                    :href="qrcode"
                    :download="`Service-${serviceId} QR`"
                    class="flex gap-x-2 text-primary text-start text-xs"
                    @click="onDownloadClick"
                  >
                    <faDownload class="w-3 fill-primary"></faDownload>
                    {{ $t('Download QR Code') }}
                  </a>
                </div>
              </div>
              <div
                class="w-32 h-32 aspect-square flex justify-center items-center rounded border border-brand-gray-300 shadow-lg"
              >
                <img :src="qrcode" alt="QR Code alt" />
              </div>
            </div>

            <div
              class="flex flex-col w-1/4 px-3 py-6 border-l border-gray-100"
              v-if="showServiceCardVideo"
            >
              <div
                class="font-normal border-b border-primary"
                @click="showVideoDaialog(serviceCardLanguage.serviceVideoURL)"
              >
                <img
                  width="211"
                  height="119"
                  src="https://img.youtube.com/vi/LDkUv5slOS0/hqdefault.jpg"
                />
              </div>
            </div>
          </div>
        </div>
        <!-- below card -->
        <StepsCard
          :label="`There are five  steps to apply for a ${serviceShortName}:`"
          :serviceChannel
        />
      </div>
    </template>
  </div>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import apiHelperService from '@/services'
import { useI18n } from 'vue-i18n'
import client_lookup from '@/assets/json/client_config.json'
import faPaperPlane from '@/assets/icons/faPaperPlane.svg?component'
import faDownload from '@/assets/icons/faDownload.svg?component'
import { useAuthStore } from '@/stores/auth'
import StepsCard from '@/components/cards/StepsCard.vue'
import useFile from '@/composables/useFile'
import { useQRCode } from '@vueuse/integrations/useQRCode'

const route = useRoute()
const i18n = useI18n()
const authStore = useAuthStore()
const lookups = client_lookup
const isLoading = ref(true)

const { showServiceCardVideo } = client_lookup

const serviceShortName = ref()
const headerText = ref({})
const serviceCardLanguage = ref()
const serviceChannel = ref()

//to implement later
// const generateQrCode = () => {
//   let localUser = authStore.user
//   let appId = localUser && localUser.applicationId
//   if (route.query?.applicationId) {
//     appId = route.query?.applicationId
//   }
//   const baseURL = import.meta.env.VITE_UAEPASS_ENDPOINTS
//   let qRCodeParam = `${baseURL}/default.html#/serviceQrCodeHandler?applicationId=${appId}&serviceTransactionId=${route.query?.serviceTransactionId}&adminRegionId=${route.query?.administrativeRegionId}&sourceChannel=${lookups.channelSource?.web}`
//   window.open(qRCodeParam, '_blank')
// }

const onDownloadClick = () => {
  useFile().downloadFile(qrcode, `Service-${route.query?.serviceTransactionId} QR`)
}

const getHeaderText = async () => {
  const response = await apiHelperService.get(
    `/api/shared/service/${route.query?.serviceTransactionId}/name`
  )
  headerText.value = response
}

const getServiceShortName = async () => {
  const params = {
    applicationId: route.query.administrativeRegionId,
    serviceTransactionId: route.query.serviceTransactionId
  }
  const response = await apiHelperService.get('/api/quick-search/service/short-name', { params })
  if (response) {
    const { serviceShortNameEnglish, serviceShortNameArabic } = response
    i18n.locale.value === 'en'
      ? (serviceShortName.value = serviceShortNameEnglish)
      : (serviceShortName.value = serviceShortNameArabic)
  }
}

const getServiceCard = async () => {
  const response = await apiHelperService.get(
    `/api/serviceCard/client/${route.query?.serviceTransactionId}`
  )
  serviceCardLanguage.value = response
}

const loadServiceChannels = async () => {
  const response = await apiHelperService.get('/api/lookup/service-channels')
  serviceChannel.value = response
}

const showServiceChannels = (channelsId) => {
  if (
    serviceCardLanguage.value?.serviceChannelIDs &&
    serviceCardLanguage.value?.serviceChannelIDs.includes(channelsId)
  )
    return true
}

const showVideoDaialog = (url) => {
  if (!url) {
    url = client_lookup.defualtServiceCommercialVideo
  }
}

const { host } = window.location
const url = `${host}/services/${route.query?.administrativeRegionId}`
const qrcode = useQRCode(url)

onMounted(async () => {
  await getServiceShortName()
  await getHeaderText()
  await getServiceCard()
  await loadServiceChannels()
  isLoading.value = false
})
</script>

<style></style>
